import { JSX, useEffect, useState } from 'react';
import BulkUserCreationStepOne from '../../views/BulkUserCreationStepOne';
import BulkUserCreationStepTwo from '../../views/BulkUserCreationStepTwo';
import { useFormik } from 'formik';
import { getBulkUserCreationSchema } from './schema';
import { useApiClients } from '../../client/api/useApiClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Department,
  GetAllDepartmentResponse,
  GetAllRolesResponse,
  Roles,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';
import BulkUserCreationStepThree, {
  BulkUserFormValues,
} from '../../views/BulkUserCreationStepThree';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useNavigate } from 'react-router';
import NoDepartment from '../../components/NoDepartment';
import ModalComponent from '../../components/ModalComponent';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { toggleModal } from '../../reducers/modalToggle';
import clsx from 'clsx';
import BulkUserUploadConfirmationModal from '../../components/BulkUserUploadConfirmationModal';
import { useLocalStorage } from '@uidotdev/usehooks';
import { UserWithOrgContext } from '../../types/user';
import { hideBodyScroll, showBodyScroll } from '../../utils/bodyScroll';

const INITIAL_STATE = {
  file: [],
};

export default function BulkUserCreationPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const modalName = useSelector((state: RootState): string => state.modalToggle.modalName);
  const [userInfo] = useLocalStorage<UserWithOrgContext | null>('userInfo', null);
  const organizationId = userInfo?.orgContext?.org.id || undefined;
  const organizationOwnerId = userInfo?.orgContext?.id || undefined;

  const dispatch = useDispatch();

  const [notExistDepartment, setNotExistDepartment] = useState<string[]>([]);
  const navigate = useNavigate();
  const { DepartmentApi, RolesApi, UserApi, BulkUploadsApi } = useApiClients();
  const [currentPage, setCurrentPage] = useState(1);
  const {
    data: allDepartments,
    isLoading: departmentsLoading,
    isRefetching,
  } = useQuery<GetAllDepartmentResponse>({
    queryKey: ['departments', organizationId],
    queryFn: async () => {
      const response =
        await DepartmentApi.departmentControllerFindAllDepartment({
          disablePagination: true,
          ...(organizationId ? { organizationId: organizationId } : {}),
        });
      return response?.data;
    },
  });

  const { data: allRoles, isLoading: rolesLoading } =
    useQuery<GetAllRolesResponse>({
      queryKey: ['roles'],
      queryFn: async () => {
        const response = await RolesApi.rolesControllerGetAllRoles();
        return response?.data;
      },
    });

  const emailAlreadyExists = useMutation({
    mutationFn: async ({ emails }: { emails: string[] }) => {
      return await UserApi.userControllerCheckEmailExist({
        checkGoupOfEmailsDto: {
          emails,
        },
      });
    },
    onSuccess: (data: any) => {
      return data?.data?.users;
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const roles =
    allRoles?.roles?.map((node: Roles) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const bulkUserCreationHandler = useFormik<BulkUserFormValues>({
    initialValues: INITIAL_STATE,
    validationSchema: getBulkUserCreationSchema(),
    onSubmit: async (values) => {
      try {
        const response =
          await BulkUploadsApi.queuesControllerInitiateBulkUpload({
            bulkUploadUserRequestDto: {
              ...(userInfo?.orgContext && { organizationId: organizationId, organizationOwnerId: organizationOwnerId }),
              users: (values as any)?.userRecords?.map((node: any) => {
                return {
                  employeeId: node?.employeeId,
                  fullName: node?.fullName,
                  email: node?.email,
                  contact: node?.contactNumber?.toString() || '',
                  dob: node?.dateOfBirth
                    ? moment(node.dateOfBirth).isValid()
                      ? moment(node.dateOfBirth).format('DD/MM/YYYY')
                      : undefined
                    : undefined,
                  dateOfHire: node?.dateOfHire
                    ? moment(node.dateOfHire).isValid()
                      ? moment(node.dateOfHire).format('DD/MM/YYYY')
                      : undefined
                    : undefined,
                  departmentId: node?.department,
                  roleId: node?.role,
                  jobTitle: node?.jobTitle,
                };
              }),
            },
          });
        toast.success(response?.data?.message);
        navigate('/task-review');
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const setAllTouched = (errors: any) => {
    const markTouched = (obj: any): any => {
      if (typeof obj !== 'object' || obj === null) {
        return true;
      }
      if (Array.isArray(obj)) {
        return obj.map((item) => markTouched(item));
      }
      return Object.keys(obj).reduce((acc: any, key) => {
        acc[key] = markTouched(obj[key]);
        return acc;
      }, {});
    };
    return markTouched(errors);
  };

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      setCurrentStep(steps);
      window.scrollTo(0, 0);
      return;
    }

    const errors = await bulkUserCreationHandler.validateForm();
    const userRecords =
      (bulkUserCreationHandler?.values as any)?.userRecords || [];

    if (currentStep === 1) {
      window.scrollTo(0, 0);
      setCurrentStep(steps);

      if (Object.keys(errors).length > 0) {
        let errorIndexes: number[] = [];
        if (Array.isArray((errors as any)?.userRecords)) {
          errorIndexes = (errors as any)?.userRecords
            .map((err: any, idx: number) =>
              err && Object.keys(err).length > 0 ? idx : -1,
            )
            .filter((idx: number) => idx !== -1);
        }
        const touched = await setAllTouched(errors);
        await bulkUserCreationHandler.setTouched(touched);
        await bulkUserCreationHandler.setErrors(errors);
        if (errorIndexes.length > 0) {
          const pageSize = 10;
          const errorIndex = errorIndexes[0];
          const pageNumber = Math.floor(errorIndex / pageSize) + 1;
          setCurrentPage(pageNumber);
        }
      }
      return;
    }

    if (Object.keys(errors).length > 0) {
      let errorIndexes: number[] = [];
      if (Array.isArray((errors as any)?.userRecords)) {
        errorIndexes = (errors as any)?.userRecords
          .map((err: any, idx: number) =>
            err && Object.keys(err).length > 0 ? idx : -1,
          )
          .filter((idx: number) => idx !== -1);
      }
      const touched = await setAllTouched(errors);
      await bulkUserCreationHandler.setTouched(touched);
      await bulkUserCreationHandler.setErrors(errors);
      if (errorIndexes.length > 0) {
        const pageSize = 10;
        const errorIndex = errorIndexes[0];
        const pageNumber = Math.floor(errorIndex / pageSize) + 1;
        setCurrentPage(pageNumber);
      }
      return;
    } else {
      const mails = userRecords.map((node: any) => node?.email || '');
      const mailsExist = await emailAlreadyExists.mutateAsync({
        emails: mails,
      });

      const existingEmails = mailsExist?.data?.users || [];

      const emailErrors = userRecords.map((record: any) => {
        if (existingEmails.includes(record.email.toLowerCase())) {
          return { email: 'Email already exists' };
        }
        return undefined;
      });

      const hasEmailErrors = emailErrors.some((err: any) => !!err);
      if (hasEmailErrors) {
        const mergedErrors: any = {
          userRecords: userRecords.map((_: any, idx: number) => {
            const emailError = emailErrors?.[idx] || {};
            return Object.keys(emailError).length > 0 ? emailError : undefined;
          }),
        };

        const touched = await setAllTouched(mergedErrors);
        await bulkUserCreationHandler.setTouched(touched);
        await bulkUserCreationHandler.setErrors(mergedErrors);

        const errorIndexes = mergedErrors.userRecords
          ?.map((err: any, idx: number) => (err ? idx : -1))
          .filter((idx: number) => idx !== -1);

        if (errorIndexes.length > 0) {
          const pageSize = 10;
          const errorIndex = errorIndexes[0];
          const pageNumber = Math.floor(errorIndex / pageSize) + 1;
          setCurrentPage(pageNumber);
        }

        return;
      }
    }

    window.scrollTo(0, 0);
    setCurrentStep(steps);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (departments?.length === 0) {
        hideBodyScroll();
      } else {
        showBodyScroll();
      }
    };

    handleScroll();
    return () => { showBodyScroll() };
  }, [departments]);

  const steps: Record<number, JSX.Element> = {
    1: (
      <BulkUserCreationStepOne
        formHandler={bulkUserCreationHandler}
        handleSteps={(step: number) => {
          handleNextStep(step);
          setCurrentPage(1);
        }}
        departments={departments}
        roles={roles}
        setNotExistDepartment={setNotExistDepartment}
      />
    ),
    2: (
      <BulkUserCreationStepTwo
        formHandler={bulkUserCreationHandler}
        handleSteps={(step: number) => handleNextStep(step)}
        departments={departments}
        setCurrentPage={setCurrentPage}
        roles={roles}
        loading={emailAlreadyExists.isPending}
        currentPage={currentPage}
        notExistDepartment={notExistDepartment}
      />
    ),
    3: (
      <BulkUserCreationStepThree
        formHandler={bulkUserCreationHandler}
        roles={roles}
        departments={departments}
        handleSteps={(step: number) => handleNextStep(step)}
      />
    ),
  };

  if (departmentsLoading || rolesLoading || isRefetching) {
    return <Loader />;
  }
  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <form
        action="#"
        className="h-full"
        onSubmit={bulkUserCreationHandler?.handleSubmit}
      >
        {steps[currentStep]}
      </form>
      {(modalName === 'bulk-user-upload-confirmation-modal') && (
        <ModalComponent
          otherClasses="px-4 sm:px-0 "
          additionalClass={clsx(
            'w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6 ',
          )}
          customClose={() => {
            dispatch(toggleModal(''));
          }}
        >
          <BulkUserUploadConfirmationModal
            formHandler={bulkUserCreationHandler}
            handleClose={() => {
              bulkUserCreationHandler.resetForm();
              dispatch(toggleModal(''));
            }}
          />

        </ModalComponent>
      )}
      {departments?.length === 0 && <NoDepartment />}
    </div>
  );
}
